import React, { useState, useEffect } from 'react';
import { useLang } from '@/i18n/LanguageContext';
import ScrollReveal from '@/components/ScrollReveal';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  const classroomImages = [
    { src: 'WhatsApp Image 2026-07-02 at 13.04.34.jpeg', campus: 'Gastonia Campus' },
    { src: '3C0FAD11-48FE-4173-BB49-FD679BD0F115.JPG', campus: 'Lake Norman Campus' },
    { src: 'IMG_0474.jpg', campus: 'Clinical Setup' },
    { src: 'IMG_0475.jpg', campus: 'Clinical Setup' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % classroomImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [classroomImages.length]);

  const faqsEn = [
    {
      q: "How long is the program?",
      a: "We offer an accelerated 12 week program. In that time you will receive the knowledge and hands-on experience to prepare you for a career in the exciting field of dental assisting."
    },
    {
      q: "What is the cost of the program?",
      a: "Application fee $50 + 12 Week Dental Assistant I Course $3,650."
    },
    {
      q: "What certificates do I graduate with after this course?",
      a: "With our program, you will receive certificates of completion in Chairside Dental Assisting, North Carolina X-ray Certification, OSHA & HIPAA, Sterilization & Infection Control, and CPR."
    },
    {
      q: "What are the basic admission requirements?",
      a: "To enroll, applicants must: be at least 18 years of age (or 17 with parent/guardian approval), provide proof of a high school transcript or G.E.D. transcript, pay the $50 application fee, and sign the enrollment agreement."
    },
    {
      q: "Does the school find us an externship office to complete hours?",
      a: "If the student cannot find an office to complete externship and can prove their efforts to find one, we will arrange for an office to complete hours needed."
    },
    {
      q: "Do you find us a job when we complete the course?",
      a: "Students are responsible for applying and interviewing. We have a network of dentists that we can assist to find a job if the student has no success on their own."
    },
    {
      q: "Do you offer weekend or evening classes?",
      a: "Yes! We understand that many of our students have busy schedules. That's why we offer Saturday classes to accommodate students who work or have other commitments during the week."
    },
    {
      q: "What kind of job placement assistance do you offer?",
      a: "We provide comprehensive job placement assistance to help you find employment after graduation. This includes resume building, interview preparation, and connecting you with dental practices in need of trained professionals."
    },
    {
      q: "Is financial aid available?",
      a: "While we don't offer traditional financial aid, we provide in-house payment plans to ensure that our programs are financially accessible to all students."
    },
    {
      q: "Do I need prior experience to enroll?",
      a: "No prior experience is necessary to enroll in our dental assistant program. Our curriculum is designed to teach you everything you need to know, from basic skills to advanced techniques, to become a successful dental assistant."
    },
    {
      q: "What is the job outlook for dental assistants?",
      a: "The demand for dental assistants is growing rapidly. Graduates from our program have a high job placement rate, and we work closely with local dental offices to help students find positions after graduation."
    }
  ];

  const faqsEs = [
    {
      q: "¿Cuánto dura el programa?",
      a: "Ofrecemos un programa acelerado de 12 semanas. En ese tiempo recibirás el conocimiento y la experiencia práctica para prepararte para una carrera en el emocionante campo de la asistencia dental."
    },
    {
      q: "¿Cuál es el costo del programa?",
      a: "Tarifa de solicitud $50 + Curso de 12 Semanas de Asistente Dental I $3,650."
    },
    {
      q: "¿Qué certificados obtengo al graduarme?",
      a: "Con nuestro programa, recibirás certificados de finalización en: Asistencia Dental, Certificación de Rayos X de Carolina del Norte, OSHA y HIPAA, Esterilización y Control de Infecciones, y RCP."
    },
    {
      q: "¿Cuáles son los requisitos básicos de admisión?",
      a: "Para inscribirse, los aspirantes deben: tener al menos 18 años (o 17 con aprobación de padre/tutor), presentar transcripción de escuela secundaria o G.E.D., pagar la tarifa de solicitud de $50 y firmar el acuerdo de inscripción."
    },
    {
      q: "¿La escuela nos busca una oficina de externado para completar las horas?",
      a: "Si el estudiante no puede encontrar una oficina para completar el externado y puede demostrar sus esfuerzos, organizaremos una oficina para completar las horas necesarias."
    },
    {
      q: "¿Nos consiguen trabajo al completar el curso?",
      a: "Los estudiantes son responsables de aplicar y entrevistarse. Tenemos una red de dentistas con quienes podemos asistir para encontrar empleo si el estudiante no tiene éxito por su cuenta."
    },
    {
      q: "¿Ofrecen clases de fin de semana o nocturnas?",
      a: "¡Sí! Entendemos que muchos de nuestros estudiantes tienen horarios ocupados. Por eso ofrecemos clases los sábados para acomodar a quienes trabajan o tienen otros compromisos durante la semana."
    },
    {
      q: "¿Qué tipo de asistencia para empleo ofrecen?",
      a: "Brindamos asistencia integral de colocación laboral para ayudarte a encontrar empleo después de graduarte. Esto incluye elaboración de currículum, preparación para entrevistas y conexión con consultorios dentales."
    },
    {
      q: "¿Hay ayuda financiera disponible?",
      a: "Aunque no ofrecemos ayuda financiera tradicional, proporcionamos planes de pago internos para asegurar que nuestros programas sean financieramente accesibles para todos los estudiantes."
    },
    {
      q: "¿Necesito experiencia previa para inscribirme?",
      a: "No se necesita experiencia previa. Nuestro plan de estudios está diseñado para enseñarte todo lo que necesitas saber, desde habilidades básicas hasta técnicas avanzadas, para convertirte en un asistente dental exitoso."
    },
    {
      q: "¿Cuál es la perspectiva laboral para los asistentes dentales?",
      a: "La demanda de asistentes dentales está creciendo rápidamente. Los graduados de nuestro programa tienen una alta tasa de colocación laboral, y trabajamos de cerca con consultorios locales para ayudar a los estudiantes a encontrar posiciones."
    }
  ];

  const faqs = lang === 'en' ? faqsEn : faqsEs;
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              {lang === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              {lang === 'en' ? 'Everything you need to know about our programs.' : 'Todo lo que necesitas saber sobre nuestros programas.'}
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={() => toggleFaq(idx)}
              >
                <div className="flex justify-between items-center p-6">
                  <h3 className="text-lg font-semibold text-slate-800 pr-8">{faq.q}</h3>
                  {openIndex === idx ? (
                    <ChevronUp className="w-5 h-5 text-brand-primary shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
                  )}
                </div>
                {openIndex === idx && (
                  <div className="px-6 pb-6 text-slate-600 bg-slate-50/50">
                    <p className="pt-2 border-t border-slate-100">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Classroom Photo Section */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              {lang === 'en' ? 'Our Classrooms & Clinical Setup' : 'Nuestras Aulas y Áreas Clínicas'}
            </h3>
            <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-lg border border-slate-200 relative aspect-video bg-slate-100">
              {classroomImages.map((item, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={`/images/gallery/${item.src}`}
                    alt={item.campus}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Campus label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-6 pb-12 pt-10 z-10">
                    <span className="text-white text-xl font-bold tracking-wide drop-shadow">
                      {item.campus}
                    </span>
                  </div>
                </div>
              ))}
              {/* Pagination Dots */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-20">
                {classroomImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImage ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <button onClick={() => navigate('/application')} className="inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white transition-all duration-200 bg-brand-primary border border-transparent rounded-full hover:bg-brand-primary/90 hover:scale-105 hover:shadow-lg hover:shadow-brand-primary/30">
              {lang === 'en' ? 'Apply Now' : 'Aplica Ahora'}
            </button>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default FAQ;
